import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import ProductItem from "../components/home/ProductItem";
import { useDispatch, useSelector } from "react-redux";
import Loading from "../components/Loading";
import {
  getFilteredProducts,
  selectFilters,
} from "../redux/reducers/productSlice";

const Products = () => {
  const [searchParams] = useSearchParams();
  const collectionParam = searchParams.get('collection');
  const [sortOption, setSortOption] = useState('popularity');
  
  const products = useSelector((state) => state.product.products);
  const loading = useSelector((state) => state.product.loading);
  const error = useSelector((state) => state.product.error);
  const errMsg = useSelector((state) => state.product.errMsg);
  const containFilters = useSelector((state) => state.product.containFilters);
  const dispatch = useDispatch();
  const filter = useSelector((state) => state.product.filter);

  // Collection metadata
  const collectionMetadata = {
    'immunity': {
      title: 'Immunity & Vitality',
      description: 'Strengthen your body\'s natural defense system with powerful herbal formulations designed to boost immunity and vitality'
    },
    'digestive': {
      title: 'Digestive Wellness',
      description: 'Support optimal gut health and digestive function with time-tested Ayurvedic remedies for complete digestive care'
    },
    'detox': {
      title: 'Detox & Cleanse',
      description: 'Purify and rejuvenate your body with natural detoxifying formulations for blood purification and kidney health'
    },
    'wellness': {
      title: 'Daily Wellness',
      description: 'Essential daily supplements for overall health including calcium, iron, and comprehensive nutritional support'
    },
    'specialty': {
      title: 'Specialty Care',
      description: 'Targeted solutions for specific health concerns including ortho care, uterine health, and skin conditions'
    },
    'juices': {
      title: 'Herbal Juices',
      description: 'Pure, concentrated herbal extracts and juices for natural wellness and daily vitality support'
    }
  };

  // Filter products by collection if collection param exists
  const displayProducts = useMemo(() => {
    let filtered = products;

    // Filter by collection
    if (collectionParam) {
      const collectionKeywords = {
        'immunity': ['immunity', 'shilajit', 'multi-active', 'brain', 'platelet'],
        'digestive': ['antacid', 'gas', 'digestion', 'liver', 'piles'],
        'detox': ['blood', 'stone', 'uric', 'kidney'],
        'wellness': ['calcium', 'iron', 'weight', 'cough', 'thyroid'],
        'specialty': ['ortho', 'uterine', 'psoriasis', 'women'],
        'juices': ['juice', 'aloe', 'nkj', 'karela']
      };

      const keywords = collectionKeywords[collectionParam] || [];
      filtered = products.filter(product => {
        const title = product.title.toLowerCase();
        return keywords.some(keyword => title.includes(keyword));
      });
    }

    // Apply sorting
    const sorted = [...filtered];
    switch (sortOption) {
      case 'newest':
        return sorted.reverse();
      case 'price-low':
        return sorted.sort((a, b) => a.price - b.price);
      case 'price-high':
        return sorted.sort((a, b) => b.price - a.price);
      case 'name-asc':
        return sorted.sort((a, b) => a.title.localeCompare(b.title));
      case 'popularity':
      default:
        return sorted;
    }
  }, [products, collectionParam, sortOption]);

  useEffect(() => {
    if (!loading) {
      dispatch(getFilteredProducts({ gender: "all" }));
      dispatch(
        selectFilters({ filter: { ...filter, color: "", company: "" } })
      );
    }
    // eslint-disable-next-line
  }, [!loading]);

  const pageTitle = collectionParam && collectionMetadata[collectionParam]
    ? collectionMetadata[collectionParam].title
    : 'All Products';

  const pageDescription = collectionParam && collectionMetadata[collectionParam]
    ? collectionMetadata[collectionParam].description
    : 'Explore our complete range of premium Ayurvedic wellness products, crafted with traditional wisdom and modern science';

  document.title = `${pageTitle} - HOLIVANA`;

  return (
    <div className="bg-[#f6f7f4] min-h-screen">
      {/* Page Header */}
      <header className="bg-white border-b border-stone-200">
        <div className="max-w-screen-xl mx-auto px-6 pt-8 pb-6">
          {/* Breadcrumb */}
          <nav className="text-xs text-stone-500 mb-3" aria-label="Breadcrumb">
            <ol className="flex items-center gap-2">
              <li><a href="/" className="hover:text-stone-900">Home</a></li>
              <li>/</li>
              {collectionParam ? (
                <>
                  <li><a href="/collections" className="hover:text-stone-900">Collections</a></li>
                  <li>/</li>
                  <li aria-current="page">{pageTitle}</li>
                </>
              ) : (
                <li aria-current="page">All Products</li>
              )}
            </ol>
          </nav>

          {/* Title & Description */}
          <div className="space-y-3 max-w-3xl">
            <h1 className="text-3xl lg:text-4xl font-light text-stone-900 tracking-tight">
              {pageTitle}
            </h1>
            <p className="text-stone-600 leading-relaxed text-lg">
              {pageDescription}
            </p>
          </div>
        </div>
      </header>

      {/* Utility Bar */}
      <div className="border-b border-stone-200 bg-white">
        <div className="max-w-screen-xl mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="text-sm text-stone-600">
              {displayProducts.length} {displayProducts.length === 1 ? 'product' : 'products'}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-sm text-stone-600">Sort:</span>
              <select 
                value={sortOption}
                onChange={(e) => setSortOption(e.target.value)}
                className="text-sm border-0 border-b border-stone-300 bg-transparent text-stone-900 focus:outline-none focus:border-emerald-700 pr-8"
              >
                <option value="popularity">Popularity</option>
                <option value="newest">Newest</option>
                <option value="price-low">Price: Low to High</option>
                <option value="price-high">Price: High to Low</option>
                <option value="name-asc">Name: A to Z</option>
              </select>
            </div>
          </div>
        </div>
      </div>

      {/* Products Grid */}
      <main className="max-w-screen-xl mx-auto px-6 py-12">
        {!error ? (
          <>
            {loading ? (
              <div className="flex items-center justify-center py-20">
                <Loading />
              </div>
            ) : (
              <>
                {displayProducts.length > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-x-6 gap-y-10">
                    {displayProducts.map((product, index) => (
                      <ProductItem
                        key={product._id}
                        product={product}
                        containFilter={containFilters[index]}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-20 text-stone-600">
                    No products found
                  </div>
                )}
              </>
            )}
          </>
        ) : (
          <div className="text-center py-20 text-stone-600">
            {errMsg}. Please reload the page.
          </div>
        )}
      </main>

      {/* Brand Values Section */}
      <section className="bg-white border-t border-stone-200">
        <div className="max-w-screen-xl mx-auto px-6 py-16">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                🌿
              </div>
              <h3 className="text-sm font-medium text-stone-900">100% Natural</h3>
              <p className="text-xs text-stone-600">Pure Ayurvedic ingredients</p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                🔬
              </div>
              <h3 className="text-sm font-medium text-stone-900">Lab Tested</h3>
              <p className="text-xs text-stone-600">Quality assured formulations</p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                🍃
              </div>
              <h3 className="text-sm font-medium text-stone-900">Sugar Free</h3>
              <p className="text-xs text-stone-600">Healthy & safe for all</p>
            </div>
            
            <div className="text-center space-y-3">
              <div className="w-12 h-12 mx-auto flex items-center justify-center text-2xl text-emerald-700">
                ✨
              </div>
              <h3 className="text-sm font-medium text-stone-900">Clinically Inspired</h3>
              <p className="text-xs text-stone-600">Traditional wisdom meets science</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Products;
